import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";

interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangeFilterProps {
  onDateRangeChange: (dateRange: DateRange) => void;
  className?: string;
}

export const DateRangeFilter = ({ onDateRangeChange, className = "" }: DateRangeFilterProps) => {
  const [selectedRange, setSelectedRange] = useState<string>("");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Date | undefined>();
  const [tempToDate, setTempToDate] = useState<Date | undefined>();

  const predefinedRanges = [
    { label: "Last 7 Days", days: 7 },
    { label: "Last 15 Days", days: 15 },
    { label: "Last 30 Days", days: 30 },
  ];

  // Store the last selected range to force re-trigger
  // Always trigger filter and API call on predefined range click
  const handlePredefinedRange = (label: string, days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    const dateRange = { from, to };
    setCustomRange(undefined);
    setTempFromDate(undefined);
    setTempToDate(undefined);
    setIsDialogOpen(false);
    onDateRangeChange(dateRange);
    // Always update selectedRange after API call to force re-render
    setTimeout(() => setSelectedRange(label), 0);
  };

  const handleCustomRange = () => {
    if (tempFromDate && tempToDate) {
      const dateRange = { from: tempFromDate, to: tempToDate };
      setCustomRange(dateRange);
      setSelectedRange("Custom Range");
      onDateRangeChange(dateRange);
      setIsDialogOpen(false);
      setTempFromDate(undefined);
      setTempToDate(undefined);
    }
  };


  const getDisplayText = () => {
    if (!selectedRange) return "";
    if (selectedRange === "Custom Range" && customRange) {
      return `${format(customRange.from, "MMM dd")} - ${format(customRange.to, "MMM dd, yyyy")}`;
    }
    return selectedRange;
  };

  // Clear filter handler
  const handleClearFilter = () => {
    setSelectedRange("");
    setCustomRange(undefined);
    setTempFromDate(undefined);
    setTempToDate(undefined);
    setIsDialogOpen(false);
    // Call parent with null to indicate clear
    onDateRangeChange(null as any);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Only show badge if a range is selected */}
      {selectedRange && (
        <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-purple-200 text-xs px-2 py-1">
          {getDisplayText()}
        </Badge>
      )}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2 min-w-[120px]">
            <Calendar className="h-4 w-4" />
            <span className="text-sm font-medium">Date Range</span>
            <ChevronDown className="h-4 w-4 ml-auto" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Select Date Range
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Predefined Ranges */}
            <div>
              <h4 className="text-sm font-medium mb-3 text-gray-700">Quick Select</h4>
              <div className="grid grid-cols-1 gap-2">
                {predefinedRanges.map((range, idx) => (
                  <Button
                    key={range.label + '-' + idx + '-' + selectedRange}
                    variant={selectedRange === range.label ? "default" : "outline"}
                    className="justify-start text-left"
                    onClick={() => handlePredefinedRange(range.label, range.days)}
                  >
                    {range.label}
                  </Button>
                ))}
              </div>
            </div>
            {/* Custom Range */}
            <div>
              <h4 className="text-sm font-medium mb-3 text-gray-700">Custom Range</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">From Date</label>
                    <Input
                      type="date"
                      value={tempFromDate ? format(tempFromDate, "yyyy-MM-dd") : ""}
                      onChange={(e) => setTempFromDate(e.target.value ? new Date(e.target.value) : undefined)}
                      max={tempToDate ? format(tempToDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">To Date</label>
                    <Input
                      type="date"
                      value={tempToDate ? format(tempToDate, "yyyy-MM-dd") : ""}
                      onChange={(e) => setTempToDate(e.target.value ? new Date(e.target.value) : undefined)}
                      min={tempFromDate ? format(tempFromDate, "yyyy-MM-dd") : undefined}
                      max={format(new Date(), "yyyy-MM-dd")}
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleCustomRange}
                  disabled={!tempFromDate || !tempToDate}
                  className="w-full"
                >
                  Apply Custom Range
                </Button>
              </div>
            </div>
            {/* Clear Filter Button */}
            <div>
              <Button onClick={handleClearFilter} variant="outline" className="w-full mt-2 text-red-600 border-red-200 hover:bg-red-50">
                Clear Filter
              </Button>
            </div>
          </div>
          {/* Current Selection Display */}
          {selectedRange && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Selection:</span>
                <Badge variant="secondary" className="ml-2">
                  {getDisplayText()}
                </Badge>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};